import { axios, axiosRetry } from '@/plugins/axios';

export function getMenuCategories(id, showAll = false, lang = null, cfg = {}) {
    const params = new URLSearchParams();

    params.append('showAll', showAll);

    if (lang) {
        params.append('lang', lang);
    }

    return axiosRetry.get(`/categories/${id}?${params.toString()}`, {
        timeout: 8000,
        ...cfg
    });
}

export function getMenuItemInventory(id, cfg = {}) {
    return axios.get(`/menus-items/${id}/inventory`, {
        ...cfg
    });
}

/**
 * Filters categories to determine which are visible in the menu.
 * Excludes categories hidden by `hiddenInMenu` and those restricted for the selected area.
 *
 * @param {Array} categories - Array of category objects to filter.
 * @param {Object|null} selectedArea - The selected area object, may be null.
 * @param {Object} options - Configuration options.
 * @param {boolean} options.excludeHidden - If true, excludes categories marked as hiddenInMenu (default: false).
 * @param {boolean} options.parentOnly - If true, excludes categories with parentCategoryId (default: false).
 * @returns {Array} - Filtered array of categories allowed to be displayed.
 */
export function filterVisibleCategories(
    categories,
    selectedArea,
    options = { excludeHidden: false, parentOnly: false }
) {
    if (!categories || !Array.isArray(categories)) {
        return categories;
    }

    const areaId = selectedArea?.id;

    if (!areaId) {
        let filteredCategories = categories;

        if (options.excludeHidden) {
            filteredCategories = filteredCategories.filter(
                cat => !cat.hiddenInMenu
            );
        }

        if (options.parentOnly) {
            filteredCategories = filteredCategories.filter(
                cat => !cat.parentCategoryId
            );
        }

        return filteredCategories;
    }

    return categories.filter(category => {
        if (options.excludeHidden && category.hiddenInMenu) return false;
        if (options.parentOnly && category.parentCategoryId) return false;

        const restricted = category.restrictedAreas;
        return !Array.isArray(restricted) || !restricted.includes(areaId);
    });
}

/**
 * Checks if a menu item is available in sufficient quantity
 * Fetches current inventory and updates `item.inventory` reactively
 * @param {Object} item - The menu item object with an `id` field
 * @param {number} requiredQty - Required quantity to check (default: 1)
 * @returns {Promise<boolean>} - True if inventory is equal or greater than quantity
 * @throws {Error} - When the API request fails
 */
export async function checkMenuItemAvailability(item, requiredQty = 1) {
    try {
        const { data: inventory } = await getMenuItemInventory(item.id);

        item.inventory = inventory;

        return inventory >= requiredQty;
    } catch (error) {
        throw new Error(`API ${error}`);
    }
}
